import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
	ShieldCheck, Bot, Globe2, Zap, Lock, Bell,
	Clock, TrendingDown, Check, ArrowRight, Menu, X,
	ChevronDown, Users, Building2, Briefcase, MessageSquare,
	Scale, Megaphone, UserX, Newspaper, Heart, Radar,
} from 'lucide-react';
import { Button } from '../components/ui';
import { PATHS } from '../constants/Paths';
import usePageMeta from '../hooks/usePageMeta';
import PilotApplicationForm from '../components/composite/PilotApplicationForm';

/**
 * LandingPage - the public marketing site.
 *
 * Rebuilt September 2026. Four things were measured on the live page and drove
 * this, rather than taste:
 *
 * 1. It was 22,046px tall on desktop and 11,174px on a phone. Thirty screens
 *    and fourteen screens. Everything past the middle was written for nobody.
 *    The stats band is gone, the roadmap is compressed, section padding is
 *    roughly halved, and one capability card was cut.
 * 2. On a phone the product mock sat about 1,450px down, so four screens of
 *    copy arrived before any evidence the thing exists. The hero now orders
 *    the mock above the trust ticks on mobile.
 * 3. Six sections opened with a coloured eyebrow pill, then a heading, then a
 *    one-line subhead, in lockstep. That repetition is the most recognisable
 *    generated-page signature there is. Sections now open with the heading.
 * 4. The page used none of the product's own components: zero Button, zero
 *    Card, every surface a hand-written class string. That is why the site and
 *    the app looked like different products. Actions use the Button primitive.
 *
 * Two other rules this page holds to: the copy addresses a person watching
 * their own account and a team watching a roster, without either feeling
 * spoken past ("the accounts you care about"); and no claim appears here that
 * the terms or the privacy policy contradict.
 */

// Dark section ground. Reused rather than pasted so the hero and the closing
// band cannot drift apart, and so the page's dark/light rhythm is one decision.
const DARK_GROUND = {
	backgroundColor: '#0A0B14',
	backgroundImage: [
		'radial-gradient(120% 90% at 12% 0%, rgba(43,42,107,0.95) 0%, rgba(43,42,107,0) 55%)',
		'radial-gradient(90% 80% at 92% 18%, rgba(18,62,85,0.85) 0%, rgba(18,62,85,0) 60%)',
		'radial-gradient(70% 70% at 60% 110%, rgba(58,29,92,0.9) 0%, rgba(58,29,92,0) 60%)',
		'linear-gradient(168deg, #101223 0%, #0A0B14 100%)',
	].join(','),
};

// A grid you feel rather than notice, masked so it fades out. Without it a
// large dark area reads as flat and dead; with it un-masked it reads as a
// template.
const GRID_OVERLAY = {
	backgroundImage:
		'linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px)',
	backgroundSize: '56px 56px',
	maskImage: 'radial-gradient(80% 60% at 30% 20%, #000 0%, transparent 75%)',
	WebkitMaskImage: 'radial-gradient(80% 60% at 30% 20%, #000 0%, transparent 75%)',
};

/**
 * Hero motion, kept in one place and scoped to this page.
 *
 * It is meant to demonstrate, not decorate. The exposure dots travel from the
 * score an account started at to the score it sits at now, which is precisely
 * what that chart encodes; the signal rows arrive one after another because
 * hearing about them as they happen is the product's promise. A viewer who has
 * asked for reduced motion gets the finished state immediately, with no
 * transition at all, rather than a slower version of the same thing.
 */
const HERO_MOTION_CSS = `
@keyframes rc-travel { from { left: var(--from); opacity: .35; } to { left: var(--to); opacity: 1; } }
@keyframes rc-arrive { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes rc-live-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(52,211,153,.55); } 50% { box-shadow: 0 0 0 4px rgba(52,211,153,0); } }
.rc-dot { left: var(--to); animation: rc-travel 900ms cubic-bezier(.22,.75,.28,1) both; }
.rc-row { animation: rc-arrive 420ms cubic-bezier(.22,.75,.28,1) both; }
.rc-live { animation: rc-live-pulse 2.4s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .rc-dot, .rc-row, .rc-live { animation: none !important; }
  .rc-dot { left: var(--to); }
  .rc-row { opacity: 1; transform: none; }
}
`;

/**
 * The landing page owns its palette and is deliberately NOT themed.
 *
 * It is a fixed composition: a dark navy hero and nav above light sections, so
 * its greys are LIGHT text on a dark ground (`text-neutral-300 hover:text-white`).
 * Running the semantic tokens through it would invert exactly those, and a
 * marketing page that flips with a viewer's app preference is not wanted.
 *
 * Hence `neutral-*` rather than `gray-*` throughout. Same values either way,
 * since both scales point at one ramp; the name is what carries the intent. In
 * this codebase `neutral-*` means "fixed on purpose" and `gray-*` means "not
 * yet migrated", which keeps the remaining-work count honest.
 */
const LandingPage = () => {
	const navigate = useNavigate();

	usePageMeta({
		title: 'RepCheck - AI-Powered Social Media Risk Monitoring',
		description:
			'RepCheck watches the accounts you care about and how people are reacting to them, flagging reputation risks on X/Twitter and TikTok before they spread. More platforms rolling out.',
	});

	const [showWaitlistModal, setShowWaitlistModal] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [openFaq, setOpenFaq] = useState(0);

	const openWaitlist = () => { setMobileMenuOpen(false); setShowWaitlistModal(true); };

	const navLinks = [
		{ label: 'What it does', href: '#what' },
		{ label: 'How it works', href: '#how' },
		{ label: 'Roadmap', href: '#roadmap' },
		{ label: 'FAQ', href: '#faq' },
	];

	// Reddit is written and deployed but refuses unauthenticated reads until a
	// developer app is registered, so it is not claimed as live.
	const platformsLive = ['X / Twitter', 'TikTok'];
	const platformsSoon = ['Reddit', 'Instagram', 'LinkedIn', 'YouTube'];

	const problems = [
		{
			icon: Clock,
			title: 'You find out too late',
			description:
				'By the time a risky post reaches your feed it is already being screenshotted. Reaction time is the whole game.',
		},
		{
			icon: TrendingDown,
			title: 'The cost is real',
			description:
				'One controversial post can stall a campaign and end a partnership, often within hours.',
		},
		{
			icon: Users,
			title: 'Counting replies tells you nothing',
			description:
				'Two hundred angry replies can mean a pile-on, or a crowd defending you. Volume alone cannot tell them apart.',
		},
	];

	// The signals a person needs to see, in the words a person would use.
	const signals = [
		{ icon: Scale, title: 'Legal threat', description: 'Someone says they are instructing lawyers, filing, or going to the police.' },
		{ icon: Megaphone, title: 'Accusation', description: 'A specific claim of wrongdoing, or someone saying they have receipts.' },
		{ icon: UserX, title: 'Boycott call', description: 'Telling other people to unfollow, cancel, or drop the sponsor.' },
		{ icon: Bot, title: 'Fake account', description: 'Somebody trading on the name of an account you watch.' },
		{ icon: Newspaper, title: 'Press', description: 'A journalist, or coverage starting to circulate.' },
		{ icon: Heart, title: 'Public backing', description: 'Someone with standing vouching for them, which is worth knowing too.' },
	];

	const features = [
		{
			icon: Bot,
			title: 'AI risk analysis',
			description:
				'Every post is scored across 11 reputation-risk categories, from hate speech and harassment to explicit content, violence and misconduct.',
		},
		{
			icon: MessageSquare,
			title: 'What the replies are saying',
			description:
				'Replies are separated into people who are with the account and people who are against it, so anger aimed at someone else is never counted against them.',
		},
		{
			icon: Globe2,
			title: 'Text, images and video',
			description:
				'Monitor X/Twitter and TikTok from one place. TikTok videos are read for what is said and what is shown, not just the caption.',
		},
		{
			icon: Zap,
			title: 'Scans that keep running',
			description:
				'Schedule recurring scans so coverage does not lapse, with live progress while a scan is running.',
		},
		{
			icon: Radar,
			title: 'RepGuard, before you post',
			badge: 'Coming soon',
			description:
				'A browser extension that checks a draft on X while it is still a draft, against a daily picture of what is landing badly right now.',
		},
		{
			icon: Lock,
			title: 'Access control and encryption',
			description:
				'Role-based permissions, encrypted in transit and at rest. Your data is never used to train AI models and never sold.',
		},
	];

	const steps = [
		{
			icon: Users,
			title: 'Add the accounts',
			description: 'Your own, or the people you look after. Add them by handle or import a CSV.',
		},
		{
			icon: Radar,
			title: 'RepCheck reads both sides',
			description: 'Scheduled scans read what those accounts post, and pulses read how people are replying.',
		},
		{
			icon: Bell,
			title: 'You hear about what matters',
			description: 'Risky posts and the replies that need a person, with the context to act on them.',
		},
	];

	const audiences = [
		{
			icon: Users,
			title: 'For creators',
			description: 'Watch your own accounts. Know how a post is landing before it turns, and see who is actually attacking you.',
		},
		{
			icon: Building2,
			title: 'For agencies',
			description: 'Every client roster in one workspace, and reports you can hand to a brand without rewriting them.',
		},
		{
			icon: Briefcase,
			title: 'For talent management',
			description: 'Protect the endorsement deals your clients depend on, and see a problem while it is still a reply.',
		},
	];

	const roadmap = [
		{
			title: 'Shipped',
			status: 'Live now',
			items: ['X/Twitter and TikTok', 'Video: speech and on-screen content', 'Risk analysis across 11 categories', 'Reply analysis and actionable signals'],
		},
		{
			title: 'Building',
			status: 'In progress',
			items: ['RepGuard browser extension for X', 'Pre-publication draft checks', 'Mentions: what is said about you elsewhere'],
		},
		{
			title: 'Next',
			status: 'Planned',
			items: ['Reddit, Instagram and LinkedIn', 'API and webhooks', 'White-label reports'],
		},
	];

	const faqs = [
		{
			q: 'How is this different from social listening tools?',
			a: 'Listening tools count how often a brand is mentioned. RepCheck reads the content itself: what the accounts you watch are posting, and what people are replying, then tells you which of those replies needs a person today. Coverage of what is said about you elsewhere is in build now.',
		},
		{
			q: "What counts as 'risky content'?",
			a: 'Posts are scored across 11 categories: hate speech, harassment, explicit content, violence, political controversy, cultural insensitivity, misconduct, drug references, self-harm, spam, and a catch-all for anything else. You can tune which ones matter most.',
		},
		{
			q: 'What are actionable signals?',
			a: 'Most replies are noise. A few are not. RepCheck marks the ones that carry a legal threat, a specific accusation, a boycott call, an impersonation attempt, press interest, or notable public backing, and only when they are aimed at the account you watch rather than at somebody else in the thread.',
		},
		{
			q: 'How quickly will I know if something is wrong?',
			a: 'Scans run on the schedule you set and analyse posts within minutes of collecting them. High-risk content raises an alert as soon as it is scored, so you are responding in minutes rather than hearing about it second-hand.',
		},
		{
			q: 'Is my data secure?',
			a: 'Everything is encrypted in transit and at rest, access is controlled with role-based permissions, and we never use your data to train AI models or sell it. We do not hold a SOC 2 or ISO 27001 certification and will not claim otherwise.',
		},
		{
			q: "What's included in the pilot?",
			a: 'Early access to the full platform, priority support, direct influence over what we build next, and pilot pricing held for 12 months. Applications are reviewed by a person.',
		},
	];

	// The hero product card. Both halves mirror real screens: the top is the
	// exposure chart from the scans page, the bottom is the actionable signals
	// lane from a heartbeat run. Invented handles, real shapes.
	const heroAccounts = [
		{ handle: 'nova.reyes', score: 78, from: 61, band: 'bg-orange-500', tone: 'text-orange-300' },
		{ handle: 'kaijames', score: 41, from: 44, band: 'bg-amber-400', tone: 'text-amber-300' },
		{ handle: 'thehollowfm', score: 12, from: 12, band: 'bg-emerald-500', tone: 'text-emerald-300' },
	];

	const signalFeed = [
		{ tag: 'Legal threat', tone: 'bg-rose-500/15 text-rose-300', text: '"My lawyers have the screenshots."', meta: '3h' },
		{ tag: 'Accusation', tone: 'bg-amber-500/15 text-amber-300', text: '"Receipts thread on the 2019 contracts"', meta: '11h' },
		{ tag: 'Boycott call', tone: 'bg-orange-500/15 text-orange-300', text: '"Unfollow and tell the sponsor why"', meta: '14h' },
		{ tag: 'Fake account', tone: 'bg-indigo-500/20 text-indigo-300', text: '"Someone is DMing people as you"', meta: '1d' },
	];

	return (
		<div className="min-h-screen bg-white font-sans text-neutral-900">
			{/* Nav */}
			<header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0B14]/90 backdrop-blur">
				<div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
					<div className="flex items-center gap-2">
						<span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-white">
							<ShieldCheck className="h-5 w-5" />
						</span>
						<span className="text-lg font-semibold tracking-tight text-white">RepCheck</span>
					</div>

					<nav className="hidden items-center gap-8 md:flex">
						{navLinks.map((l) => (
							<a key={l.href} href={l.href} className="text-sm text-neutral-300 transition-colors hover:text-white">{l.label}</a>
						))}
					</nav>

					<div className="hidden items-center gap-2 md:flex">
						<button onClick={() => navigate(PATHS.LOGIN)} className="rounded-md px-3 py-2 text-sm font-medium text-neutral-300 transition-colors hover:text-white">
							Sign in
						</button>
						<Button onClick={openWaitlist} size="sm">Apply for the pilot</Button>
					</div>

					<button className="md:hidden text-neutral-200" onClick={() => setMobileMenuOpen((v) => !v)} aria-label="Menu">
						{mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
					</button>
				</div>

				{mobileMenuOpen && (
					<div className="border-t border-white/10 bg-[#0A0B14] px-4 py-4 md:hidden">
						<div className="flex flex-col gap-3">
							{navLinks.map((l) => (
								<a key={l.href} href={l.href} onClick={() => setMobileMenuOpen(false)} className="py-1 text-neutral-300">{l.label}</a>
							))}
							<button onClick={() => { setMobileMenuOpen(false); navigate(PATHS.LOGIN); }} className="py-1 text-left text-neutral-300">Sign in</button>
							<Button onClick={openWaitlist} className="mt-1 w-full">Apply for the pilot</Button>
						</div>
					</div>
				)}
			</header>

			{/* Hero. Asymmetric on desktop; on mobile the product comes before the
			    trust ticks, because it used to sit four screens down.

			    The motion here is meant to demonstrate rather than decorate: the
			    exposure dots travel from where each account started to where it is
			    now, which is exactly what that chart encodes, and the signals
			    arrive one after another because arriving as they happen is the
			    product's whole promise. Both are suppressed entirely under
			    prefers-reduced-motion. */}
			<section className="relative isolate overflow-hidden" style={DARK_GROUND}>
				<style>{HERO_MOTION_CSS}</style>

				{/* Ambient shapes rather than another grid: three soft ellipses at
				    different sizes and an off-centre ring, so the ground has
				    geometry that is not all right angles. */}
				<div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-[0.35]" style={GRID_OVERLAY} />
				<div
					aria-hidden
					className="pointer-events-none absolute -right-40 -top-32 -z-10 h-[30rem] w-[30rem] rounded-full blur-[2px]"
					style={{ background: 'radial-gradient(circle at 35% 35%, rgba(99,102,241,.5), rgba(99,102,241,0) 62%)' }}
				/>
				<div
					aria-hidden
					className="pointer-events-none absolute -left-32 top-1/3 -z-10 h-[22rem] w-[22rem] rounded-full blur-[2px]"
					style={{ background: 'radial-gradient(circle at 60% 40%, rgba(20,184,166,.28), rgba(20,184,166,0) 65%)' }}
				/>
				<div
					aria-hidden
					className="pointer-events-none absolute bottom-[-8rem] left-1/3 -z-10 h-[26rem] w-[26rem] rounded-full blur-[3px]"
					style={{ background: 'radial-gradient(circle at 45% 55%, rgba(147,51,234,.24), rgba(147,51,234,0) 62%)' }}
				/>
				<div
					aria-hidden
					className="pointer-events-none absolute -right-24 top-24 -z-10 hidden h-[34rem] w-[34rem] rounded-full border border-white/[0.06] lg:block"
				/>

				<div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-[1.02fr_.98fr] lg:items-center lg:gap-12">
					<div>
						<h1 className="text-[2rem] font-bold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-[3.4rem]">
							Catch reputation risks{' '}
							<span className="text-brand-300">before they go viral</span>
						</h1>
						<p className="mt-5 max-w-xl text-lg leading-relaxed text-neutral-300">
							Posts do not go viral on their own. RepCheck watches the accounts you care about and how
							people are reacting to them, then tells you what needs attention today.
						</p>

						<div className="mt-7 flex flex-col gap-3 sm:flex-row">
							<Button onClick={openWaitlist} size="lg" className="w-full sm:w-auto">
								Apply for the pilot <ArrowRight className="ml-2 h-5 w-5" />
							</Button>
							<button
								onClick={() => navigate(PATHS.LOGIN)}
								className="inline-flex h-12 w-full items-center justify-center rounded-md border border-white/20 px-6 text-lg font-medium text-neutral-200 transition-colors hover:bg-white/5 sm:w-auto"
							>
								Sign in
							</button>
						</div>

						<div className="mt-7 hidden flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-400 lg:flex">
							<span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-brand-400" /> Set up in minutes</span>
							<span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-brand-400" /> No credit card</span>
							<span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-brand-400" /> Pilot pricing held for 12 months</span>
						</div>
						<p className="mt-4 hidden text-sm text-neutral-400 lg:block">
							For creators watching their own accounts, and the teams who look after them.
						</p>
					</div>

					{/* The product, with depth. Two real screens stacked. */}
					<div className="min-w-0 lg:pl-4">
						<div className="rounded-xl border border-white/[0.13] bg-white/[0.045] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.85)] backdrop-blur-sm lg:[transform:perspective(1400px)_rotateY(-9deg)_rotateX(2.5deg)] lg:[transform-origin:left_center]">
							{/* Exposure, mirroring the scans page */}
							<div className="border-b border-white/[0.08] p-3">
								<div className="mb-2 flex items-center justify-between px-1 text-xs">
									<span className="font-medium text-neutral-300">Exposure by account</span>
									<span className="inline-flex items-center gap-1.5 text-neutral-400">
										<span className="rc-live h-1.5 w-1.5 rounded-full bg-emerald-400" /> Live
									</span>
								</div>
								{heroAccounts.map((a, i) => (
									<div key={a.handle} className="mb-1.5 grid grid-cols-[5.5rem_1fr_2rem] items-center gap-2 last:mb-0">
										<span className="truncate text-[11px] text-neutral-400">@{a.handle}</span>
										<span className="relative block h-1.5">
											<span className="absolute inset-0 flex overflow-hidden rounded-full">
												<span className="h-full bg-emerald-500/[0.16]" style={{ width: '35%' }} />
												<span className="h-full bg-amber-400/[0.18]" style={{ width: '25%' }} />
												<span className="h-full bg-orange-500/[0.18]" style={{ width: '25%' }} />
												<span className="h-full bg-red-500/[0.18]" style={{ width: '15%' }} />
											</span>
											{/* Travels from the first score to the current one. */}
											<span
												className={`rc-dot absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-[#0f1020] ${a.band}`}
												style={{ '--from': `${a.from}%`, '--to': `${a.score}%`, animationDelay: `${240 + i * 130}ms` }}
											/>
										</span>
										<span className={`text-right text-[11px] font-semibold tabular-nums ${a.tone}`}>{a.score}</span>
									</div>
								))}
							</div>

							{/* Signals, mirroring a heartbeat run */}
							<div className="p-3">
								<div className="mb-2 flex items-center justify-between px-1 text-xs">
									<span className="font-medium text-neutral-300">Actionable signals</span>
									<span className="text-neutral-400">Today</span>
								</div>
								{signalFeed.map((row, i) => (
									<div
										key={row.tag}
										className="rc-row mb-1.5 flex min-w-0 items-center gap-2.5 rounded-lg bg-white/[0.05] px-2.5 py-2 last:mb-0"
										style={{ animationDelay: `${560 + i * 160}ms` }}
									>
										<span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${row.tone}`}>{row.tag}</span>
										<span className="min-w-0 flex-1 truncate text-xs text-neutral-200">{row.text}</span>
										<span className="ml-auto shrink-0 text-[11px] text-neutral-400">{row.meta}</span>
									</div>
								))}
							</div>
						</div>
					</div>

					{/* Mobile-only trust row, after the product. */}
					<div className="flex flex-col gap-2 text-sm text-neutral-400 lg:hidden">
						<span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-brand-400" /> Set up in minutes, no credit card</span>
						<span className="inline-flex items-center gap-2"><Check className="h-4 w-4 text-brand-400" /> Pilot pricing held for 12 months</span>
						<p className="mt-1 text-neutral-400">For creators watching their own accounts, and the teams who look after them.</p>
					</div>
				</div>

				{/* Platform strip, inside the dark band so it costs no extra height. */}
				<div className="border-t border-white/10">
					<div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-4 text-sm sm:px-6">
						<span className="text-neutral-400">Live on</span>
						{platformsLive.map((p) => (
							<span key={p} className="inline-flex items-center gap-1.5 text-neutral-200">
								<Check className="h-4 w-4 text-brand-400" /> {p}
							</span>
						))}
						<span className="text-neutral-400">Rolling out: {platformsSoon.join(', ')}</span>
					</div>
				</div>
			</section>

			{/* Problem */}
			<section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
				<h2 className="max-w-2xl text-3xl font-bold tracking-tight sm:text-4xl">
					Your reputation is in someone else&apos;s hands
				</h2>
				<p className="mt-3 max-w-2xl text-lg text-neutral-600">
					And you usually find out after it has already spread.
				</p>
				<div className="mt-10 grid gap-6 sm:grid-cols-3">
					{problems.map((p) => (
						<div key={p.title}>
							<span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100 text-neutral-700">
								<p.icon className="h-5 w-5" />
							</span>
							<h3 className="mt-4 font-semibold">{p.title}</h3>
							<p className="mt-1.5 text-neutral-600">{p.description}</p>
						</div>
					))}
				</div>
			</section>

			{/* What people say back, plus the signals. The one genuinely new section. */}
			<section id="what" className="border-y border-neutral-100 bg-neutral-50/70">
				<div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
					<div className="grid gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
						<div>
							<h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
								A scanner reads one side of the conversation
							</h2>
							<p className="mt-4 text-lg leading-relaxed text-neutral-600">
								RepCheck reads the replies too. It separates the people who are with an account from the
								people who are against it, and tells the difference between anger aimed at your client
								and anger on their behalf. Those look identical in a word count and mean opposite things.
							</p>
							<p className="mt-4 text-lg leading-relaxed text-neutral-600">
								So when a post takes off, you can see whether it is being attacked or defended before you
								decide whether to say anything at all.
							</p>
						</div>

						<div>
							<h3 className="font-semibold">Most replies are noise. These are not.</h3>
							<div className="mt-4 grid gap-x-8 gap-y-4 sm:grid-cols-2">
								{signals.map((s) => (
									<div key={s.title} className="flex gap-3">
										<s.icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
										<div>
											<div className="font-medium text-neutral-900">{s.title}</div>
											<p className="mt-0.5 text-sm text-neutral-600">{s.description}</p>
										</div>
									</div>
								))}
							</div>
							<p className="mt-5 text-sm text-neutral-500">
								Flagged only when aimed at the account you watch. Anger at a politician on their own post
								is not their problem, and we do not count it as one.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* Capabilities */}
			<section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
				<h2 className="text-3xl font-bold tracking-tight sm:text-4xl">What you get</h2>
				<div className="mt-10 grid gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
					{features.map((f) => (
						<div key={f.title}>
							<span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
								<f.icon className="h-5 w-5" />
							</span>
							<h3 className="mt-4 flex flex-wrap items-center gap-2 font-semibold">
								{f.title}
								{f.badge && (
									<span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">{f.badge}</span>
								)}
							</h3>
							<p className="mt-1.5 text-neutral-600">{f.description}</p>
						</div>
					))}
				</div>
			</section>

			{/* How it works */}
			<section id="how" className="border-y border-neutral-100 bg-neutral-50/70">
				<div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
					<h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Set up in minutes</h2>
					<div className="mt-10 grid gap-8 sm:grid-cols-3">
						{steps.map((s, i) => (
							<div key={s.title}>
								<div className="flex items-center gap-3">
									<span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-600 text-white">
										<s.icon className="h-5 w-5" />
									</span>
									{/* The number is real: these happen in order. */}
									<span className="text-sm font-medium text-neutral-400">Step {i + 1}</span>
								</div>
								<h3 className="mt-4 font-semibold">{s.title}</h3>
								<p className="mt-1.5 text-neutral-600">{s.description}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Who it's for */}
			<section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
				<h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Who it is for</h2>
				<div className="mt-10 grid gap-6 sm:grid-cols-3">
					{audiences.map((a) => (
						<div key={a.title} className="rounded-xl border border-neutral-200 p-6">
							<a.icon className="h-6 w-6 text-brand-600" />
							<h3 className="mt-4 font-semibold">{a.title}</h3>
							<p className="mt-1.5 text-neutral-600">{a.description}</p>
						</div>
					))}
				</div>
			</section>

			{/* Roadmap */}
			<section id="roadmap" className="border-y border-neutral-100 bg-neutral-50/70">
				<div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
					<h2 className="text-3xl font-bold tracking-tight sm:text-4xl">What we are building</h2>
					<div className="mt-10 grid gap-6 sm:grid-cols-3">
						{roadmap.map((col) => (
							<div key={col.title} className="rounded-xl border border-neutral-200 bg-white p-6">
								<div className="flex items-baseline justify-between">
									<h3 className="font-semibold">{col.title}</h3>
									<span className="text-xs text-neutral-500">{col.status}</span>
								</div>
								<ul className="mt-4 space-y-2 text-sm text-neutral-600">
									{col.items.map((it) => (
										<li key={it} className="flex gap-2">
											<Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" /> {it}
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section id="faq" className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
				<h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Questions, answered</h2>
				<div className="mt-8 divide-y divide-neutral-200 border-y border-neutral-200">
					{faqs.map((f, i) => (
						<div key={f.q}>
							<button
								onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
								className="flex w-full items-center justify-between gap-4 py-4 text-left"
								aria-expanded={openFaq === i}
							>
								<span className="font-medium">{f.q}</span>
								<ChevronDown className={`h-5 w-5 shrink-0 text-neutral-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
							</button>
							{openFaq === i && <p className="pb-5 text-neutral-600">{f.a}</p>}
						</div>
					))}
				</div>
			</section>

			{/* Closing call to action, on the same dark ground as the hero. */}
			<section className="relative isolate overflow-hidden" style={DARK_GROUND}>
				<div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-40" style={GRID_OVERLAY} />
				<div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-20">
					<h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Do not wait for the next crisis</h2>
					<p className="mt-4 text-lg text-neutral-300">
						The pilot is open, and applications are reviewed by a person.
					</p>
					<div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
						<Button onClick={openWaitlist} size="lg" className="w-full sm:w-auto">
							Apply for the pilot <ArrowRight className="ml-2 h-5 w-5" />
						</Button>
						<button
							onClick={() => navigate(PATHS.LOGIN)}
							className="inline-flex h-12 w-full items-center justify-center rounded-md border border-white/20 px-6 text-lg font-medium text-neutral-200 transition-colors hover:bg-white/5 sm:w-auto"
						>
							Sign in
						</button>
					</div>
				</div>
			</section>

			{/* Footer */}
			<footer className="bg-[#0A0B14] text-neutral-400">
				<div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
					<div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
						<div className="lg:col-span-2">
							<div className="flex items-center gap-2">
								<span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-white">
									<ShieldCheck className="h-5 w-5" />
								</span>
								<span className="text-lg font-semibold text-white">RepCheck</span>
							</div>
							<p className="mt-4 max-w-sm text-sm">
								Reputation monitoring for the accounts you care about. See what they post, see how people
								react, and hear about a problem while it is still small.
							</p>
						</div>
						<div>
							<h4 className="text-sm font-semibold text-white">Product</h4>
							<ul className="mt-4 space-y-3 text-sm">
								<li><a href="#what" className="transition-colors hover:text-white">What it does</a></li>
								<li><a href="#how" className="transition-colors hover:text-white">How it works</a></li>
								<li><a href="#roadmap" className="transition-colors hover:text-white">Roadmap</a></li>
							</ul>
						</div>
						<div>
							<h4 className="text-sm font-semibold text-white">Account</h4>
							<ul className="mt-4 space-y-3 text-sm">
								<li><button onClick={() => navigate(PATHS.LOGIN)} className="transition-colors hover:text-white">Sign in</button></li>
								<li><button onClick={openWaitlist} className="transition-colors hover:text-white">Apply for the pilot</button></li>
								<li><button onClick={() => navigate(PATHS.SUPPORT)} className="transition-colors hover:text-white">Support</button></li>
							</ul>
						</div>
					</div>
					<div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-neutral-800 pt-8 text-sm sm:flex-row">
						<p>&copy; 2026 RepCheck. All rights reserved.</p>
						<div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
							<Link to={PATHS.PRIVACY} className="transition-colors hover:text-white">Privacy Policy</Link>
							<Link to={PATHS.TERMS} className="transition-colors hover:text-white">Terms of Service</Link>
							<span className="inline-flex items-center gap-2 text-neutral-500">
								<ShieldCheck className="h-4 w-4 text-brand-500" /> Encrypted, private by design
							</span>
						</div>
					</div>
				</div>
			</footer>

			{/* Application modal. Scrolls on a short screen rather than clipping its
			    own top off, which a centred fixed-height modal does on a phone. */}
			{showWaitlistModal && (
				<div
					className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-neutral-950/60 p-4 backdrop-blur-sm sm:items-center"
					onClick={() => setShowWaitlistModal(false)}
				>
					<div className="relative my-auto w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
						<button
							onClick={() => setShowWaitlistModal(false)}
							className="absolute right-4 top-4 rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
							aria-label="Close"
						>
							<X className="h-5 w-5" />
						</button>

						<div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
							<ShieldCheck className="h-6 w-6" />
						</div>
						<h3 className="mt-4 text-2xl font-bold tracking-tight text-neutral-900">Apply for the pilot</h3>
						<p className="mt-2 text-neutral-600">
							Tell us a little about what you would use RepCheck for. We review every application and reply
							by email.
						</p>

						<PilotApplicationForm submitLabel="Send application" />
					</div>
				</div>
			)}
		</div>
	);
};

export default LandingPage;
